import React, { useState, useEffect, useMemo } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
  User,
  Mail,
  Phone,
  Building2,
  MapPin,
  Globe,
  Camera,
  Save,
  AlertCircle,
  CheckCircle,
  Edit3,
  Crown,
  HelpCircle,
  LogOut,
  Trash2,
  Monitor,
} from 'lucide-react';
import { Container } from '../styles/GlobalStyles';
import { theme } from '../styles/theme';
import { AuthAPI } from '../services/api';
import { KnowledgeBaseApi } from '../services/knowledgeBaseApi';
import { uploadService } from '../services/uploadService';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import Particles from '../components/Particles';

// =============================================================================
// MOTION
// =============================================================================

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulseRed = keyframes`
  0%, 100% { color: ${theme.colors.error[600]}; }
  50%      { color: ${theme.colors.error[700]}; }
`;

// =============================================================================
// PAGE SHELL
// =============================================================================

const ProfileContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing[8]} 0 ${theme.spacing[16]};
  margin: 30px 30px 0;
  border: 1px solid ${theme.colors.border};
  border-radius: 20px;
  position: relative;
  /* clip (not hidden) — hidden would establish a scroll container and break
     position: sticky on the ActionBar at the bottom of the form card. */
  overflow: clip;

  @media (max-width: ${theme.breakpoints.md}) {
    margin: 20px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    margin: 0;
    border-radius: 0;
    border: none;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  max-width: 880px;
  margin: 0 auto;
  padding: 0 ${theme.spacing[4]};
  animation: ${fadeUp} 0.45s ease-out both;
`;

// =============================================================================
// PAGE HEADER
// =============================================================================

const PageHeader = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[8]};
  padding-bottom: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.borderLight};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing[4]};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Crumb = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${theme.colors.primary[500]};
  }
`;

const Title = styled.h1`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1.15;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.textSecondary};
  margin: ${theme.spacing[1]} 0 0;
  max-width: 540px;
`;

const CompletionPill = styled.div<{ $pct: number }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.full};
  box-shadow: ${theme.shadows.sm};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  white-space: nowrap;

  strong {
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.semibold};
  }

  &::before {
    content: '';
    width: 36px;
    height: 4px;
    border-radius: ${theme.borderRadius.full};
    background: linear-gradient(
      to right,
      ${theme.colors.primary[500]} 0%,
      ${theme.colors.primary[500]} ${({ $pct }) => $pct}%,
      ${theme.colors.borderLight} ${({ $pct }) => $pct}%,
      ${theme.colors.borderLight} 100%
    );
    transition: background 0.4s ease;
  }
`;

// =============================================================================
// CARD SHELL
// =============================================================================

const ProfileCard = styled.section`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.sm};
  position: relative;

  & + & {
    margin-top: ${theme.spacing[6]};
  }
`;

// =============================================================================
// HERO (avatar + identity)
// =============================================================================

const HeroPanel = styled.div`
  position: relative;
  padding: ${theme.spacing[10]} ${theme.spacing[8]} ${theme.spacing[8]};
  border-radius: ${theme.borderRadius.xl} ${theme.borderRadius.xl} 0 0;
  background:
    radial-gradient(120% 80% at 100% 0%, rgba(255, 255, 255, 0.10) 0%, transparent 60%),
    linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[700]} 100%);
  color: #fff;
  overflow: hidden;
  border-bottom: 1px solid ${theme.colors.primary[700]};

  /* Subtle grain to add tactile depth on the deep navy. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.045 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
    opacity: 0.7;
    pointer-events: none;
    mix-blend-mode: screen;
  }

  /* Soft glowing accent in the corner. */
  &::after {
    content: '';
    position: absolute;
    top: -120px;
    right: -120px;
    width: 360px;
    height: 360px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.18) 0%, transparent 65%);
    pointer-events: none;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[8]} ${theme.spacing[5]} ${theme.spacing[6]};
  }
`;

const HeroLayout = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[8]};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing[5]};
  }
`;

const AvatarStack = styled.div`
  position: relative;
  width: 140px;
  height: 140px;
  flex-shrink: 0;
`;

const AvatarRing = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
  pointer-events: none;
`;

const Avatar = styled.div<{ $hasImage?: boolean }>`
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  bottom: 8px;
  border-radius: ${theme.borderRadius.full};
  background: ${({ $hasImage }) =>
    $hasImage
      ? 'transparent'
      : `linear-gradient(135deg, ${theme.colors.primary[300]} 0%, ${theme.colors.primary[500]} 100%)`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-family: ${theme.typography.fontFamily.display};
  font-weight: ${theme.typography.fontWeight.bold};
  font-size: 38px;
  letter-spacing: -0.02em;
  border: 4px solid #fff;
  box-shadow:
    0 12px 28px rgba(0, 0, 41, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
  background-image: ${({ $hasImage }) => ($hasImage ? 'var(--avatar-url)' : 'none')};
  background-size: cover;
  background-position: center;
  user-select: none;
`;

const AvatarUpload = styled.button`
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.primary[500]};
  color: #fff;
  border: 3px solid #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${theme.shadows.md};
  transition: transform ${theme.transitions.base}, background ${theme.transitions.base};
  z-index: 2;

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[700]};
    transform: scale(1.08);
  }

  &:active:not(:disabled) {
    transform: scale(0.96);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const AvatarSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const Identity = styled.div`
  flex: 1;
  min-width: 0;
`;

const IdentityEyebrow = styled.div`
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: ${theme.spacing[2]};
`;

const DisplayName = styled.h2`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 32px;
  font-weight: ${theme.typography.fontWeight.bold};
  color: #fff;
  margin: 0 0 ${theme.spacing[1]};
  letter-spacing: -0.025em;
  line-height: 1.1;

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: 26px;
  }
`;

const RoleLine = styled.div`
  font-size: ${theme.typography.fontSize.base};
  color: rgba(255, 255, 255, 0.78);
  margin-bottom: ${theme.spacing[4]};
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};

  & .sep {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.4);
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 6px ${theme.spacing[3]};
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  color: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);

  & code {
    font-family: ${theme.typography.fontFamily.mono};
    font-size: 11px;
    color: #fff;
  }
`;

// =============================================================================
// SUB-SECTION SCAFFOLDING
// =============================================================================

const FormSection = styled.div`
  padding: ${theme.spacing[8]};

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[6]} ${theme.spacing[5]};
  }
`;

const SubSection = styled.div<{ $first?: boolean }>`
  ${({ $first }) =>
    $first
      ? css`
          padding-top: 0;
        `
      : css`
          margin-top: ${theme.spacing[10]};
          padding-top: ${theme.spacing[8]};
          border-top: 1px solid ${theme.colors.borderLight};
        `}
`;

const Eyebrow = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${theme.colors.primary[500]};
  margin-bottom: ${theme.spacing[2]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  & .num {
    color: ${theme.colors.textMuted};
    font-variant-numeric: tabular-nums;
  }
`;

const SubHeading = styled.h3`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[1]};
  letter-spacing: -0.015em;
`;

const SubHelper = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin: 0 0 ${theme.spacing[6]};
  max-width: 580px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing[5]} ${theme.spacing[6]};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[4]};
  }
`;

const FormGroup = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  ${({ $fullWidth }) => $fullWidth && css`grid-column: 1 / -1;`}
`;

const FormLabel = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  & .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: ${theme.borderRadius.sm};
    background: ${theme.colors.primary[50]};
    color: ${theme.colors.primary[500]};
  }
`;

const inputBase = css`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  background: ${theme.colors.surface};
  color: ${theme.colors.textPrimary};
  font-family: inherit;
  transition: all ${theme.transitions.base};
  width: 100%;

  &:hover:not(:disabled) {
    border-color: ${theme.colors.borderHover};
    background: ${theme.colors.surfaceHover};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    background: ${theme.colors.surface};
    box-shadow:
      0 0 0 3px ${theme.colors.primary[500]}1a,
      0 1px 2px rgba(0, 0, 0, 0.04);
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }

  &:disabled {
    background: ${theme.colors.backgroundTertiary};
    color: ${theme.colors.textMuted};
    cursor: not-allowed;
  }
`;

const FormInput = styled.input`
  ${inputBase}
`;

const FormTextArea = styled.textarea`
  ${inputBase}
  min-height: 130px;
  resize: vertical;
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const FieldHint = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
  line-height: 1.5;
`;

// =============================================================================
// CHARACTER COUNTER
// =============================================================================

const CounterWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[3]};
  margin-top: ${theme.spacing[1]};
`;

const CounterTrack = styled.div`
  flex: 1;
  height: 3px;
  background: ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
`;

const CounterFill = styled.div<{ $pct: number; $tone: 'normal' | 'warn' | 'danger' }>`
  height: 100%;
  width: ${({ $pct }) => Math.min($pct, 100)}%;
  background: ${({ $tone }) =>
    $tone === 'danger'
      ? theme.colors.error[500]
      : $tone === 'warn'
      ? theme.colors.warning[500]
      : theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.full};
  transition: width 0.25s ease, background-color 0.25s ease;
`;

const CounterLabel = styled.span<{ $tone: 'normal' | 'warn' | 'danger' }>`
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  font-family: ${theme.typography.fontFamily.mono};
  white-space: nowrap;

  ${({ $tone }) =>
    $tone === 'danger'
      ? css`
          color: ${theme.colors.error[600]};
          font-weight: ${theme.typography.fontWeight.semibold};
          animation: ${pulseRed} 1.4s ease-in-out infinite;
        `
      : $tone === 'warn'
      ? css`
          color: ${theme.colors.warning[700]};
          font-weight: ${theme.typography.fontWeight.medium};
        `
      : css`
          color: ${theme.colors.textMuted};
        `}
`;

interface CharCounterProps {
  value: string;
  max: number;
}

const CharCounter: React.FC<CharCounterProps> = ({ value, max }) => {
  const len = value.length;
  const pct = max > 0 ? (len / max) * 100 : 0;
  const tone: 'normal' | 'warn' | 'danger' = pct >= 95 ? 'danger' : pct >= 80 ? 'warn' : 'normal';
  return (
    <CounterWrap>
      <CounterTrack>
        <CounterFill $pct={pct} $tone={tone} />
      </CounterTrack>
      <CounterLabel $tone={tone}>
        {len.toLocaleString()} / {max.toLocaleString()}
      </CounterLabel>
    </CounterWrap>
  );
};

// =============================================================================
// STICKY ACTION BAR
// =============================================================================

const ActionBar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 5;
  padding: ${theme.spacing[4]} ${theme.spacing[8]};
  border-top: 1px solid ${theme.colors.borderLight};
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  border-radius: 0 0 ${theme.borderRadius.xl} ${theme.borderRadius.xl};
  box-shadow: 0 -8px 24px -16px rgba(0, 0, 41, 0.12);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[4]};

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[3]} ${theme.spacing[5]};
    flex-direction: column-reverse;
    align-items: stretch;
  }
`;

const ActionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  min-width: 0;
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${theme.colors.success[500]};
  flex-shrink: 0;
`;

const StatusMessage = styled.div<{ $type: 'success' | 'error' }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 6px ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};

  ${({ $type }) =>
    $type === 'success'
      ? css`
          background: ${theme.colors.success[100]};
          color: ${theme.colors.success[700]};
        `
      : css`
          background: ${theme.colors.error[100]};
          color: ${theme.colors.error[700]};
        `}
`;

const SaveButton = styled.button<{ $loading?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: #fff;
  border: 1px solid ${theme.colors.primary[600]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: ${({ $loading }) => ($loading ? 'wait' : 'pointer')};
  opacity: ${({ $loading }) => ($loading ? 0.85 : 1)};
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base},
    background ${theme.transitions.base};
  box-shadow: 0 6px 14px -6px rgba(0, 0, 41, 0.45);

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[700]};
    transform: translateY(-1px);
    box-shadow: 0 10px 18px -6px rgba(0, 0, 41, 0.55);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 4px 10px -6px rgba(0, 0, 41, 0.45);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const ButtonSpinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

// =============================================================================
// SUBSCRIPTION CARD
// =============================================================================

const ProSubscriptionPanel = styled.div`
  position: relative;
  padding: ${theme.spacing[8]};
  border-radius: ${theme.borderRadius.lg};
  background:
    radial-gradient(80% 100% at 0% 0%, rgba(255, 255, 255, 0.10) 0%, transparent 60%),
    linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.primary[800]} 100%);
  color: #fff;
  overflow: hidden;
  isolation: isolate;
  border: 1px solid ${theme.colors.primary[700]};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.04 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
    opacity: 0.7;
    pointer-events: none;
    mix-blend-mode: screen;
    z-index: -1;
  }
`;

const ProBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 6px ${theme.spacing[3]};
  background: rgba(255, 255, 255, 0.95);
  color: ${theme.colors.primary[700]};
  border-radius: ${theme.borderRadius.full};
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.bold};
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin-bottom: ${theme.spacing[5]};
  box-shadow: 0 6px 18px -6px rgba(0, 0, 0, 0.4);

  & svg {
    color: ${theme.colors.warning[600]};
  }
`;

const ProTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 28px;
  font-weight: ${theme.typography.fontWeight.bold};
  color: #fff;
  margin: 0 0 ${theme.spacing[2]};
  letter-spacing: -0.02em;
  line-height: 1.15;
`;

const ProBlurb = styled.p`
  color: rgba(255, 255, 255, 0.78);
  font-size: ${theme.typography.fontSize.base};
  line-height: ${theme.typography.lineHeight.relaxed};
  max-width: 460px;
  margin: 0 0 ${theme.spacing[6]};
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 ${theme.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  font-size: ${theme.typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.92);

  & .check {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: ${theme.borderRadius.full};
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
  }
`;

const ProActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const ProPrimaryCTA = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  background: #fff;
  color: ${theme.colors.primary[700]};
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: not-allowed;
  opacity: 0.78;
  transition: all ${theme.transitions.base};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }
`;

const ProGhostNote = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: rgba(255, 255, 255, 0.6);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: ${theme.typography.fontWeight.medium};
`;

// =============================================================================
// SYSTEM / DANGER CARD
// =============================================================================

const SystemSection = styled.div`
  padding: ${theme.spacing[8]};

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[6]} ${theme.spacing[5]};
  }
`;

const SectionHeader = styled.div`
  margin-bottom: ${theme.spacing[6]};
`;

const SectionTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[1]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  letter-spacing: -0.015em;

  & .icon {
    width: 32px;
    height: 32px;
    border-radius: ${theme.borderRadius.md};
    background: ${theme.colors.primary[50]};
    color: ${theme.colors.primary[500]};
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const SectionMeta = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  margin: 0;
  padding-left: 44px;
`;

const SystemActionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[8]};
`;

const SystemActionRow = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: not-allowed;
  text-align: left;
  width: 100%;
  opacity: 0.7;
  transition: all ${theme.transitions.base};

  & .lead {
    width: 36px;
    height: 36px;
    border-radius: ${theme.borderRadius.md};
    background: ${theme.colors.backgroundTertiary};
    color: ${theme.colors.textSecondary};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  & .body {
    flex: 1;
    min-width: 0;
  }

  & .heading {
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.medium};
  }

  & .desc {
    display: block;
    margin-top: 2px;
    font-size: ${theme.typography.fontSize.xs};
    color: ${theme.colors.textMuted};
    font-weight: ${theme.typography.fontWeight.normal};
  }

  & .badge {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: ${theme.colors.textMuted};
    font-weight: ${theme.typography.fontWeight.semibold};
    padding: 4px ${theme.spacing[2]};
    border-radius: ${theme.borderRadius.sm};
    background: ${theme.colors.backgroundTertiary};
  }
`;

const DangerZone = styled.div`
  padding: ${theme.spacing[6]};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.error[500]}33;
  border-left: 3px solid ${theme.colors.error[500]};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: ${theme.spacing[5]};
    right: ${theme.spacing[5]};
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${theme.colors.error[500]};
    box-shadow: 0 0 0 4px ${theme.colors.error[100]};
  }
`;

const DangerHeading = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[2]};

  & .icon {
    width: 32px;
    height: 32px;
    border-radius: ${theme.borderRadius.md};
    background: ${theme.colors.error[100]};
    color: ${theme.colors.error[600]};
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  & h4 {
    font-family: ${theme.typography.fontFamily.display};
    font-size: ${theme.typography.fontSize.base};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.textPrimary};
    margin: 0;
  }
`;

const DangerCopy = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin: 0 0 ${theme.spacing[5]};
  padding-left: 44px;
`;

const DangerCTA = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-left: 44px;
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: transparent;
  color: ${theme.colors.error[700]};
  border: 1px solid ${theme.colors.error[500]}55;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: not-allowed;
  opacity: 0.7;
  transition: all ${theme.transitions.base};

  &:hover:not(:disabled) {
    background: ${theme.colors.error[500]};
    color: #fff;
    border-color: ${theme.colors.error[500]};
  }
`;

// =============================================================================
// LOADING
// =============================================================================

const LoadingShell = styled.div`
  padding: ${theme.spacing[16]} ${theme.spacing[6]};
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[4]};
`;

const LoadingSpinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid ${theme.colors.borderLight};
  border-top-color: ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

// =============================================================================
// TYPES
// =============================================================================

interface ProfileData {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  job_title: string;
  organization_name: string;
  phone_number: string;
  bio: string;
  avatar_url: string;
  timezone: string;
  language: string;
}

const BIO_MAX = 500;
const COMPANY_OVERVIEW_MAX = 5000;
const MISSION_MAX = 2000;

// =============================================================================
// COMPONENT
// =============================================================================

const ProfilePage: React.FC = () => {
  const { state, dispatch } = useAuth();
  const { showToast } = useToast();
  const [profileData, setProfileData] = useState<Partial<ProfileData>>({});
  // Company description fields live on a separate Mongo collection (company_profiles)
  // and reach the AI's prompts via the KB profile endpoint, not the user-profile one.
  // Kept in their own state so the save path can fan out to two distinct APIs.
  const [companyProfile, setCompanyProfile] = useState<{ company_overview: string; mission_statement: string }>({
    company_overview: '',
    mission_statement: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Wired by the camera button: open the hidden <input type="file"> picker.
  const handleAvatarPickerOpen = () => {
    if (avatarUploading) return;
    fileInputRef.current?.click();
  };

  // Validate -> upload to Cloudinary/S3 -> persist via updateUserProfile so
  // the URL survives a page reload. Optimistic local update on success.
  const handleAvatarFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast({ type: 'error', title: 'Please choose an image file (PNG, JPG, GIF, WebP).' });
      return;
    }
    const MAX_BYTES = 5 * 1024 * 1024; // 5 MB cap — bigger doesn't help for a 120px avatar
    if (file.size > MAX_BYTES) {
      showToast({ type: 'error', title: 'Image is too large. Maximum size is 5 MB.' });
      return;
    }

    try {
      setAvatarUploading(true);
      const url = await uploadService.uploadImage(file);
      // Persist immediately so the new avatar isn't lost if the user navigates
      // away before pressing Save Changes.
      const persistRes = await AuthAPI.updateUserProfile({ avatar_url: url });
      if (!persistRes.success) {
        throw new Error(persistRes.error || 'Failed to save the new photo to your profile.');
      }
      setProfileData(prev => ({ ...prev, avatar_url: url }));
      // Update the global auth context so the sidebar/header re-render
      // with the new photo without requiring a page refresh.
      if (state.user) {
        dispatch({ type: 'UPDATE_USER', payload: { ...state.user, avatar_url: url } });
      }
      showToast({ type: 'success', title: 'Profile photo updated.' });
    } catch (err: any) {
      console.error('Avatar upload failed', err);
      showToast({ type: 'error', title: err?.message || 'Could not upload the photo. Please try again.' });
    } finally {
      setAvatarUploading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const initializeProfile = async () => {
      if (state.user && !profileLoaded) {
        try {
          setLoading(true);
          const [response, companyRes] = await Promise.all([
            AuthAPI.getUserProfile(),
            KnowledgeBaseApi.getProfile().catch(() => null),
          ]);

          if (!isMounted) return; // Component unmounted, don't update state

          // Soft-load the company description so a missing/erroring KB profile
          // never blocks the user-profile UI from rendering.
          if (companyRes && companyRes.success && companyRes.data) {
            setCompanyProfile({
              company_overview: companyRes.data.company_overview || '',
              mission_statement: companyRes.data.mission_statement || '',
            });
          }

          if (response.success) {
            setProfileData(response.data);
            // Mirror avatar_url into the auth context so the sidebar/header
            // pick up the photo on every page load — not only right after
            // a fresh upload.
            if (state.user && response.data?.avatar_url && response.data.avatar_url !== state.user.avatar_url) {
              dispatch({
                type: 'UPDATE_USER',
                payload: { ...state.user, avatar_url: response.data.avatar_url },
              });
            }
          } else {
            // Initialize with basic user data from auth context if profile doesn't exist
            setProfileData({
              email: state.user.email,
              first_name: state.user.first_name || '',
              last_name: state.user.last_name || '',
              job_title: '',
              organization_name: '',
              phone_number: '',
              bio: '',
              timezone: 'UTC',
              language: 'en',
            });
            showToast({
              type: 'info',
              title: 'Profile data not found. You can create your profile below.',
            });
          }
        } catch (error: any) {
          if (!isMounted) return;

          console.error('Profile loading error:', error);
          // For 500 errors or other backend issues, still allow profile creation
          setProfileData({
            email: state.user.email,
            first_name: state.user.first_name || '',
            last_name: state.user.last_name || '',
            job_title: '',
            organization_name: '',
            phone_number: '',
            bio: '',
            timezone: 'UTC',
            language: 'en',
          });
          showToast({
            type: 'warning',
            title: 'Unable to load existing profile. You can update your information below.',
          });
        } finally {
          if (isMounted) {
            setLoading(false);
            setProfileLoaded(true);
          }
        }
      } else if (!state.user) {
        setLoading(false);
      }
    };

    initializeProfile();

    return () => {
      isMounted = false;
    };
  }, [state.user, profileLoaded]);

  const handleInputChange = (field: string, value: string) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear status message when user starts editing
    if (status) {
      setStatus(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);

    try {
      // Remove fields that shouldn't be updated via profile endpoint
      const { id, email, ...updateData } = profileData;

      // Two writes — to two separate collections. Keeping them in parallel
      // means the spinner doesn't show "saved" while half the work is still
      // in flight. Errors on either side surface together.
      const [response, companyRes] = await Promise.all([
        AuthAPI.updateUserProfile(updateData),
        KnowledgeBaseApi.updateProfile({
          company_overview: companyProfile.company_overview,
          mission_statement: companyProfile.mission_statement,
        }).catch(err => ({ success: false, error: err?.message || 'Company profile save failed' })),
      ]);

      if (response.success) {
        if (!companyRes.success) {
          // Soft-fail the company half — surface a warning but don't block
          // the user-profile success path. Common cause: KB profile route
          // unavailable in dev when the backend hasn't been restarted.
          showToast({ type: 'warning', title: 'Profile saved, but company description failed to update.' });
        }
        setStatus({ type: 'success', message: 'Profile updated successfully!' });
        showToast({ type: 'success', title: 'Profile updated successfully' });
        // Profile data is already updated in the form, no need to reload
      } else {
        setStatus({ type: 'error', message: response.error || 'Failed to update profile' });
        showToast({ type: 'error', title: response.error || 'Failed to update profile' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Failed to update profile' });
      showToast({ type: 'error', title: 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  const getDisplayName = () => {
    const firstName = profileData.first_name?.trim() || '';
    const lastName = profileData.last_name?.trim() || '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    return fullName || profileData.email?.split('@')[0] || 'User';
  };

  const getInitials = () => {
    const firstName = profileData.first_name?.trim() || '';
    const lastName = profileData.last_name?.trim() || '';

    if (firstName && lastName) {
      return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
    } else if (firstName || lastName) {
      const name = firstName || lastName;
      return name.length > 1
        ? (name.charAt(0) + name.charAt(1)).toUpperCase()
        : name.charAt(0).toUpperCase();
    } else if (profileData.email) {
      return (
        profileData.email.charAt(0).toUpperCase() +
        (profileData.email.split('@')[0]?.charAt(1)?.toUpperCase() || '')
      );
    }
    return 'UN';
  };

  // Profile completion: how many of the meaningful fields are populated.
  // Drives the hero ring and the header pill — gives the user a sense of
  // progress without nagging.
  const completion = useMemo(() => {
    const checks = [
      profileData.first_name,
      profileData.last_name,
      profileData.phone_number,
      profileData.job_title,
      profileData.organization_name,
      profileData.bio,
      profileData.avatar_url,
      companyProfile.company_overview,
      companyProfile.mission_statement,
    ];
    const filled = checks.filter(v => typeof v === 'string' && v.trim().length > 0).length;
    return { filled, total: checks.length, pct: Math.round((filled / checks.length) * 100) };
  }, [profileData, companyProfile]);

  // Avatar ring progress geometry.
  const RING_RADIUS = 66;
  const RING_CIRC = 2 * Math.PI * RING_RADIUS;
  const ringOffset = RING_CIRC * (1 - completion.pct / 100);

  if (loading) {
    return (
      <ProfileContainer>
        <Particles />
        <Container>
          <ContentWrapper>
            <LoadingShell>
              <LoadingSpinner />
              Loading your profile…
            </LoadingShell>
          </ContentWrapper>
        </Container>
      </ProfileContainer>
    );
  }

  return (
    <ProfileContainer>
      <Particles />
      <Container>
        <ContentWrapper>
          <PageHeader>
            <HeaderLeft>
              <Crumb>Account</Crumb>
              <Title>Account Settings</Title>
              <Subtitle>
                Manage your personal information and the company description the AI uses when drafting tender responses.
              </Subtitle>
            </HeaderLeft>
            <CompletionPill $pct={completion.pct} aria-label={`Profile ${completion.pct}% complete`}>
              <span>
                <strong>{completion.pct}%</strong> complete
              </span>
            </CompletionPill>
          </PageHeader>

          {/* ===================== HERO + FORM CARD ===================== */}
          <ProfileCard>
            <HeroPanel>
              <HeroLayout>
                <AvatarStack>
                  <AvatarRing viewBox="0 0 140 140" aria-hidden="true">
                    <circle
                      cx="70"
                      cy="70"
                      r={RING_RADIUS}
                      fill="none"
                      stroke="rgba(255,255,255,0.18)"
                      strokeWidth="4"
                    />
                    <circle
                      cx="70"
                      cy="70"
                      r={RING_RADIUS}
                      fill="none"
                      stroke="#fff"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeDasharray={RING_CIRC}
                      strokeDashoffset={ringOffset}
                      style={{ transition: 'stroke-dashoffset 0.6s ease' }}
                    />
                  </AvatarRing>
                  <Avatar
                    $hasImage={!!profileData.avatar_url}
                    style={
                      profileData.avatar_url
                        ? ({ '--avatar-url': `url(${profileData.avatar_url})` } as React.CSSProperties)
                        : undefined
                    }
                  >
                    {!profileData.avatar_url && getInitials()}
                  </Avatar>
                  <AvatarUpload
                    type="button"
                    onClick={handleAvatarPickerOpen}
                    disabled={avatarUploading}
                    aria-label="Change profile photo"
                    title="Change profile photo"
                  >
                    {avatarUploading ? <AvatarSpinner /> : <Camera size={18} />}
                  </AvatarUpload>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    onChange={handleAvatarFileChange}
                    style={{ display: 'none' }}
                  />
                </AvatarStack>

                <Identity>
                  <IdentityEyebrow>Signed in</IdentityEyebrow>
                  <DisplayName>{getDisplayName()}</DisplayName>
                  <RoleLine>
                    {profileData.job_title || 'Add a job title'}
                    {(profileData.job_title || profileData.organization_name) && <span className="sep" />}
                    {profileData.organization_name || 'Add an organisation'}
                  </RoleLine>
                  <ChipRow>
                    <Chip>
                      <Mail size={12} />
                      <code>{profileData.email}</code>
                    </Chip>
                    <Chip>
                      <CheckCircle size={12} />
                      {completion.filled} of {completion.total} fields
                    </Chip>
                    <Chip>
                      <Crown size={12} />
                      Free plan
                    </Chip>
                  </ChipRow>
                </Identity>
              </HeroLayout>
            </HeroPanel>

            <FormSection>
              {/* ----------- 01 IDENTITY ----------- */}
              <SubSection $first>
                <Eyebrow>
                  <span className="num">01</span>
                  <span>·</span>
                  <span>Identity</span>
                </Eyebrow>
                <SubHeading>Who you are</SubHeading>
                <SubHelper>Your name and how we'll address you across the app.</SubHelper>

                <FormGrid>
                  <FormGroup>
                    <FormLabel>
                      <span className="icon">
                        <User size={12} />
                      </span>
                      First name
                    </FormLabel>
                    <FormInput
                      type="text"
                      value={profileData.first_name || ''}
                      onChange={e => handleInputChange('first_name', e.target.value)}
                      placeholder="Enter your first name"
                    />
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>
                      <span className="icon">
                        <User size={12} />
                      </span>
                      Last name
                    </FormLabel>
                    <FormInput
                      type="text"
                      value={profileData.last_name || ''}
                      onChange={e => handleInputChange('last_name', e.target.value)}
                      placeholder="Enter your last name"
                    />
                  </FormGroup>
                </FormGrid>
              </SubSection>

              {/* ----------- 02 CONTACT ----------- */}
              <SubSection>
                <Eyebrow>
                  <span className="num">02</span>
                  <span>·</span>
                  <span>Contact</span>
                </Eyebrow>
                <SubHeading>How to reach you</SubHeading>
                <SubHelper>Your email is the address you signed in with — get in touch if you need it changed.</SubHelper>

                <FormGrid>
                  <FormGroup>
                    <FormLabel>
                      <span className="icon">
                        <Mail size={12} />
                      </span>
                      Email address
                    </FormLabel>
                    <FormInput
                      type="email"
                      value={profileData.email || ''}
                      disabled
                      title="Email cannot be changed"
                    />
                    <FieldHint>Tied to your sign-in. Contact support to change.</FieldHint>
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>
                      <span className="icon">
                        <Phone size={12} />
                      </span>
                      Phone number
                    </FormLabel>
                    <FormInput
                      type="tel"
                      value={profileData.phone_number || ''}
                      onChange={e => handleInputChange('phone_number', e.target.value)}
                      placeholder="+44 20 7946 0958"
                    />
                  </FormGroup>
                </FormGrid>
              </SubSection>

              {/* ----------- 03 WORK ----------- */}
              <SubSection>
                <Eyebrow>
                  <span className="num">03</span>
                  <span>·</span>
                  <span>Work</span>
                </Eyebrow>
                <SubHeading>What you do</SubHeading>
                <SubHelper>Surfaces in the wizard hero and on activity logs across the app.</SubHelper>

                <FormGrid>
                  <FormGroup>
                    <FormLabel>
                      <span className="icon">
                        <Edit3 size={12} />
                      </span>
                      Job title
                    </FormLabel>
                    <FormInput
                      type="text"
                      value={profileData.job_title || ''}
                      onChange={e => handleInputChange('job_title', e.target.value)}
                      placeholder="e.g. Bid Manager"
                    />
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>
                      <span className="icon">
                        <Building2 size={12} />
                      </span>
                      Organisation
                    </FormLabel>
                    <FormInput
                      type="text"
                      value={profileData.organization_name || ''}
                      onChange={e => handleInputChange('organization_name', e.target.value)}
                      placeholder="Your company name"
                    />
                  </FormGroup>
                </FormGrid>
              </SubSection>

              {/* ----------- 04 LOCALISATION ----------- */}
              <SubSection>
                <Eyebrow>
                  <span className="num">04</span>
                  <span>·</span>
                  <span>Localisation</span>
                </Eyebrow>
                <SubHeading>Region & language</SubHeading>
                <SubHelper>
                  Used for date formatting, currency hints, and the language the AI defaults to when drafting.
                </SubHelper>

                <FormGrid>
                  <FormGroup>
                    <FormLabel>
                      <span className="icon">
                        <MapPin size={12} />
                      </span>
                      Timezone
                    </FormLabel>
                    <FormInput
                      type="text"
                      value={profileData.timezone || 'UTC'}
                      onChange={e => handleInputChange('timezone', e.target.value)}
                      placeholder="e.g. Europe/London"
                    />
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>
                      <span className="icon">
                        <Globe size={12} />
                      </span>
                      Language
                    </FormLabel>
                    <FormInput
                      type="text"
                      value={profileData.language || 'en'}
                      onChange={e => handleInputChange('language', e.target.value)}
                      placeholder="en"
                    />
                  </FormGroup>
                </FormGrid>
              </SubSection>

              {/* ----------- 05 ABOUT ----------- */}
              <SubSection>
                <Eyebrow>
                  <span className="num">05</span>
                  <span>·</span>
                  <span>About you</span>
                </Eyebrow>
                <SubHeading>Biography</SubHeading>
                <SubHelper>A short personal blurb shown alongside your name in shared workspaces.</SubHelper>

                <FormGroup>
                  <FormTextArea
                    value={profileData.bio || ''}
                    onChange={e => handleInputChange('bio', e.target.value)}
                    placeholder="A few sentences about your background, focus areas, or what you bring to a bid team…"
                    maxLength={BIO_MAX}
                  />
                  <CharCounter value={profileData.bio || ''} max={BIO_MAX} />
                </FormGroup>
              </SubSection>

              {/* ----------- 06 COMPANY DESCRIPTION ----------- */}
              <SubSection>
                <Eyebrow>
                  <span className="num">06</span>
                  <span>·</span>
                  <span>Company description</span>
                </Eyebrow>
                <SubHeading>How the AI describes your company</SubHeading>
                <SubHelper>
                  Short summaries used to pre-fill identity questions on tender drafts. For long-form documents
                  (capability statements, full company history), upload them in the Knowledge Base instead.
                </SubHelper>

                <FormGroup style={{ marginBottom: theme.spacing[6] }}>
                  <FormLabel>
                    <span className="icon">
                      <Edit3 size={12} />
                    </span>
                    Company overview
                  </FormLabel>
                  <FormTextArea
                    value={companyProfile.company_overview}
                    onChange={e => {
                      setCompanyProfile(prev => ({ ...prev, company_overview: e.target.value }));
                      if (status) setStatus(null);
                    }}
                    placeholder="Describe your company, its capabilities, and what makes it distinctive…"
                    maxLength={COMPANY_OVERVIEW_MAX}
                    style={{ minHeight: 180 }}
                  />
                  <CharCounter value={companyProfile.company_overview} max={COMPANY_OVERVIEW_MAX} />
                </FormGroup>

                <FormGroup>
                  <FormLabel>
                    <span className="icon">
                      <Edit3 size={12} />
                    </span>
                    Mission statement
                  </FormLabel>
                  <FormTextArea
                    value={companyProfile.mission_statement}
                    onChange={e => {
                      setCompanyProfile(prev => ({ ...prev, mission_statement: e.target.value }));
                      if (status) setStatus(null);
                    }}
                    placeholder="The mission and vision that drive your organisation…"
                    maxLength={MISSION_MAX}
                  />
                  <CharCounter value={companyProfile.mission_statement} max={MISSION_MAX} />
                </FormGroup>
              </SubSection>
            </FormSection>

            {/* Sticky save bar — anchored inside the card so it follows the user as they scroll. */}
            <ActionBar>
              <ActionMeta>
                {status ? (
                  <StatusMessage $type={status.type}>
                    {status.type === 'success' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                    {status.message}
                  </StatusMessage>
                ) : (
                  <>
                    <Dot />
                    All changes saved to the profile collections directly.
                  </>
                )}
              </ActionMeta>
              <SaveButton onClick={handleSave} $loading={saving} disabled={saving} type="button">
                {saving ? <ButtonSpinner /> : <Save size={16} />}
                {saving ? 'Saving…' : 'Save changes'}
              </SaveButton>
            </ActionBar>
          </ProfileCard>

          {/* ===================== SUBSCRIPTION ===================== */}
          <ProfileCard>
            <ProSubscriptionPanel>
              <ProBadge>
                <Crown size={12} />
                OpenTender Pro
              </ProBadge>
              <ProTitle>Unlock every winning bid.</ProTitle>
              <ProBlurb>
                Move beyond the basics with deeper analysis, unlimited document processing, and the priority
                support your bid team deserves.
              </ProBlurb>
              <FeatureList>
                <FeatureItem>
                  <span className="check">
                    <CheckCircle size={12} />
                  </span>
                  Advanced AI analysis with multi-pass scoring and refinement
                </FeatureItem>
                <FeatureItem>
                  <span className="check">
                    <CheckCircle size={12} />
                  </span>
                  Unlimited tender uploads, response generations, and exports
                </FeatureItem>
                <FeatureItem>
                  <span className="check">
                    <CheckCircle size={12} />
                  </span>
                  Priority support and early access to new features
                </FeatureItem>
              </FeatureList>
              <ProActions>
                <ProPrimaryCTA disabled type="button">
                  Learn more
                  <Crown size={14} />
                </ProPrimaryCTA>
                <ProGhostNote>Coming soon</ProGhostNote>
              </ProActions>
            </ProSubscriptionPanel>
          </ProfileCard>

          {/* ===================== SYSTEM & DANGER ===================== */}
          <ProfileCard>
            <SystemSection>
              <SectionHeader>
                <SectionTitle>
                  <span className="icon">
                    <HelpCircle size={18} />
                  </span>
                  System &amp; sessions
                </SectionTitle>
                <SectionMeta>
                  Signed in as <strong style={{ color: theme.colors.textSecondary }}>{profileData.email}</strong>
                </SectionMeta>
              </SectionHeader>

              <SystemActionList>
                <SystemActionRow disabled type="button">
                  <span className="lead">
                    <LogOut size={16} />
                  </span>
                  <span className="body">
                    <span className="heading">Sign out of all sessions</span>
                    <span className="desc">Revoke access on every device this account is signed into.</span>
                  </span>
                  <span className="badge">Soon</span>
                </SystemActionRow>

                <SystemActionRow disabled type="button">
                  <span className="lead">
                    <Monitor size={16} />
                  </span>
                  <span className="body">
                    <span className="heading">Active devices</span>
                    <span className="desc">See where you're currently signed in and remove individual sessions.</span>
                  </span>
                  <span className="badge">Soon</span>
                </SystemActionRow>
              </SystemActionList>

              <DangerZone>
                <DangerHeading>
                  <span className="icon">
                    <Trash2 size={16} />
                  </span>
                  <h4>Delete account</h4>
                </DangerHeading>
                <DangerCopy>
                  Permanently delete your account and all associated data — tender analyses, project history, and
                  knowledge-base documents will be irrecoverable.
                </DangerCopy>
                <DangerCTA disabled type="button">
                  <Trash2 size={14} />
                  Delete account
                </DangerCTA>
              </DangerZone>
            </SystemSection>
          </ProfileCard>
        </ContentWrapper>
      </Container>
    </ProfileContainer>
  );
};

export default ProfilePage;
